import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '../services/api';
import { Bell } from 'lucide-react';
import type { Notification } from '../types';
import NotificationCenter, { notificationsKey } from './NotificationCenter';

/**
 * The bell in the TopBar (desktop and mobile). It owns the unread badge and
 * opens the NotificationCenter drawer (T-48; was a popover with 10 rows).
 *
 * Badge behaviour is unchanged: the count of rows `GET /notifications`
 * returns (unread, at most 50), "9+" above nine, refreshed every 15 s.
 */
export default function NotificationBell({ organizationId, onOpenPreferences }: {
  /** FIX-NOTIF N-4 - the portal passes the organization chosen there (several organizations). */
  organizationId?: string;
  /** FIX-NOTIF N-3 - the portal opens the preferences inside the portal. */
  onOpenPreferences?: () => void;
} = {}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const countId = useId();

  const { data } = useQuery({
    queryKey: notificationsKey(organizationId),
    queryFn: () => (organizationId ? api.getNotifications({ organization_id: organizationId }) : api.getNotifications()),
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });

  const notifications: Notification[] = data?.data ?? [];
  const unreadCount = notifications.length;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('aria.notifications')}
        aria-describedby={unreadCount > 0 ? countId : undefined}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="btn-icon relative text-text-2 hover:bg-surface-subtle hover:text-text transition-colors duration-micro"
      >
        <Bell size={18} strokeWidth={1.75} aria-hidden="true" />
        {unreadCount > 0 && (
          <>
            <span
              className="absolute -top-0.5 -end-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-danger-600 px-1 text-xs font-semibold leading-none text-white tabular-nums ring-2 ring-canvas"
              aria-hidden="true"
            >
              {/* Drawn by CSS: the count is not part of the visible label (WCAG 2.5.3); the
                  sr-only line below carries it. */}
              <bdi dir="ltr" data-count={unreadCount > 9 ? '9+' : String(unreadCount)} className="after:content-[attr(data-count)]" />
            </span>
            <span id={countId} className="sr-only">{t('shell.notifications.unread', { count: unreadCount })}</span>
          </>
        )}
      </button>

      <NotificationCenter open={open} onClose={() => setOpen(false)} organizationId={organizationId} onOpenPreferences={onOpenPreferences} />
    </>
  );
}
